import { useQuery } from '@tanstack/react-query';
import { getUsageMembers, getUsagePeriods, getUsageSummary, getUsageTeams } from './usage.api';
import type {
  UsageMembersParams,
  UsagePeriodsParams,
  UsageSummaryParams,
  UsageTeamsParams,
} from './usage.types';

const USAGE_ROOT = ['core', 'usage'] as const;

export const USAGE_QUERY_KEYS = {
  ALL: USAGE_ROOT,
  SUMMARY: (p: UsageSummaryParams) => [...USAGE_ROOT, 'summary', p] as const,
  PERIODS: (p: UsagePeriodsParams) => [...USAGE_ROOT, 'periods', p] as const,
  MEMBERS: (p: UsageMembersParams) => [...USAGE_ROOT, 'members', p] as const,
  TEAMS: (p: UsageTeamsParams) => [...USAGE_ROOT, 'teams', p] as const,
};

export function useUsageSummaryQuery(p: UsageSummaryParams, enabled = true) {
  return useQuery({
    queryKey: USAGE_QUERY_KEYS.SUMMARY(p),
    queryFn: () => getUsageSummary(p),
    enabled: enabled && Boolean(p.scopePublicId),
  });
}

export function useUsagePeriodsQuery(p: UsagePeriodsParams, enabled = true) {
  return useQuery({
    queryKey: USAGE_QUERY_KEYS.PERIODS(p),
    queryFn: () => getUsagePeriods(p),
    enabled: enabled && Boolean(p.scopePublicId),
  });
}

export function useUsageMembersQuery(p: UsageMembersParams, enabled = true) {
  return useQuery({
    queryKey: USAGE_QUERY_KEYS.MEMBERS(p),
    queryFn: () => getUsageMembers(p),
    enabled: enabled && (p.scopeType === 'all' || Boolean(p.scopePublicId)),
    // Keep rows while paging/sorting, but never across a scope or period change.
    placeholderData: (previous, previousQuery) => {
      const prev = previousQuery?.queryKey[3] as UsageMembersParams | undefined;
      const same =
        prev?.scopeType === p.scopeType &&
        prev.scopePublicId === p.scopePublicId &&
        prev.periodStartDate === p.periodStartDate &&
        prev.from === p.from &&
        prev.to === p.to &&
        prev.grain === p.grain;
      return same ? previous : undefined;
    },
  });
}

export function useUsageTeamsQuery(p: UsageTeamsParams, enabled = true) {
  return useQuery({
    queryKey: USAGE_QUERY_KEYS.TEAMS(p),
    queryFn: () => getUsageTeams(p),
    enabled: enabled && Boolean(p.organizationPublicId),
    // Never show another organization's or period's rows while loading.
    placeholderData: (previous, previousQuery) => {
      const prev = previousQuery?.queryKey[3] as UsageTeamsParams | undefined;
      const same =
        prev?.organizationPublicId === p.organizationPublicId &&
        prev.periodStartDate === p.periodStartDate;
      return same ? previous : undefined;
    },
  });
}